// components/previews/PDFPreview.jsx
import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.mjs',
//   import.meta.url,
// ).toString();

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDFPreview = ({ url, filename }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [pageWidth, setPageWidth] = useState(null);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    setError('Failed to load PDF: ' + error.message);
    setLoading(false);
  }, []);

  const onPageLoadSuccess = useCallback((page) => {
    setPageWidth(page.width);
  }, []);

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(numPages || 1, prev + 1));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 2.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  const goToPage = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 1 && pageNum <= numPages) {
      setPageNumber(pageNum);
    }
  };

  return (
    <div className="bg-gray-100 rounded-lg overflow-hidden min-h-96">
      {/* PDF Controls Header */}
      <div className="bg-white p-4 border-b flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-600">PDF Preview</p>
          {numPages && (
            <span className="text-xs text-gray-500">
              ({numPages} pages)
            </span>
          )}
        </div>
        
        {/* Navigation Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Page Navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="px-2 py-1 text-xs bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
              title="Previous page"
            >
              ‹
            </button>
            
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="1"
                max={numPages || 1}
                value={pageNumber}
                onChange={(e) => goToPage(e.target.value)}
                className="w-12 px-1 py-1 text-xs text-center border rounded"
              />
              <span className="text-xs text-gray-600">
                of {numPages || '?'}
              </span>
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={!numPages || pageNumber >= numPages}
              className="px-2 py-1 text-xs bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
              title="Next page"
            >
              ›
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-1 border-l pl-2">
            <button
              onClick={zoomOut}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
              title="Zoom out"
            >
              −
            </button>
            <span className="text-xs text-gray-600 min-w-12 text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
              title="Zoom in"
            >
              +
            </button>
            <button
              onClick={resetZoom}
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              title="Reset zoom"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* PDF Content Area */}
      <div className="relative">
        {/* Loading State */}
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-90">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Loading PDF...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 text-center">
            <div className="text-red-500">
              <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="font-medium">Failed to load PDF</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* PDF Document */}
        <div className="p-4 overflow-auto max-h-96 flex justify-center">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading=""
            error=""
            className="flex justify-center"
          >
            <div className="shadow-lg">
              <Page
                pageNumber={pageNumber}
                scale={scale}
                onLoadSuccess={onPageLoadSuccess}
                loading=""
                error=""
                className="border border-gray-300"
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </div>
          </Document>
        </div>

        {/* Page Info Footer */}
        {numPages && !loading && !error && (
          <div className="bg-white border-t px-4 py-2 text-center">
            <p className="text-xs text-gray-500">
              Page {pageNumber} of {numPages} • {filename}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFPreview;